import { zodResolver } from "@hookform/resolvers/zod";
import { Check, PenLine, X } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { FieldValues, Path, PathValue, useForm } from "react-hook-form";
import { ZodTypeAny } from "zod";

import {
  FormSelectSingle,
  SelectMulti,
} from "../custom-form-fields/FormSelect";
import FormWrapper from "../custom-form-fields/FormWrapper";
import InputField from "../custom-form-fields/InputField";
import { TimePicker12 } from "../time-picker/time-picker-12hour";

import { Button } from "@/components/ui/button";
import { Side } from "@/components/ui/customChervonIcon";
import { Form, FormField, FormLabel } from "@/components/ui/form";
import { cn } from "@/lib/utils";

type ConfigProps = {
  isTable?: boolean;
  editPosition?: Side;
  label?: string;
  required?: boolean;
  showEdit: boolean;
};
interface SingleDataEditorProps<T extends FieldValues> {
  schema: ZodTypeAny;
  name: Path<T>;
  type: "text" | "time" | "number" | "select" | "multiSelect" | "textarea";
  list?: { label: string; value: string }[];
  data: PathValue<T, Path<T>>;
  children: React.ReactNode;
  handleSubmit: (data: T) => void;
  config?: ConfigProps;
  selectMultiNode?: {
    position: "right";
    node: React.ComponentType<{ id: string }>;
  };
  parentControl?: {
    isMutate: boolean;
    handleEdit: () => void;
    handleEditCancel: () => void;
  };
  classes?: {
    containerClassName?: string;
    childClassName?: string;
  };
}

export const EditButton = ({
  config,
  handleEditClick,
}: {
  config: ConfigProps;
  handleEditClick?: () => void;
}): React.JSX.Element => {
  return (
    <div
      className={cn(
        "hidden   cursor-pointer",
        !config?.isTable ? "group-hover:block" : "[td:hover_&]:block",
      )}
      onClick={handleEditClick}
    >
      <PenLine size={18} />
    </div>
  );
};

export const SingleDataEditor = <T extends FieldValues>(
  props: SingleDataEditorProps<T>,
): React.JSX.Element => {
  const {
    schema,
    name,
    type,
    list,
    data,
    selectMultiNode,
    children,
    handleSubmit,
    config = { isTable: true, editPosition: "right", showEdit: true },
    parentControl,
    classes,
  } = props;

  const form = useForm<T>({
    resolver: zodResolver(schema),
  });

  const [isMutate, setIsMutate] = useState(false);

  // Use useCallback to memoize these handlers
  const handleExit = useCallback((): void => {
    form.reset();
    if (parentControl) {
      parentControl.handleEditCancel();
    } else {
      setIsMutate(false);
    }
  }, [form, parentControl]);

  const onSubmit = (data: T): void => {
    // handleSubmit(data as T[typeof name]);
    handleSubmit(data);
    handleExit();
  };

  // Set form value when editing starts
  useEffect(() => {
    if (name && data && parentControl ? parentControl?.isMutate : isMutate) {
      // this should not be required but for multiselect it is being needed due to react selector

      if (type === "multiSelect") {
        form.setValue(
          name,
          data.map((d: string[]) => ({ label: d, value: d })),
        );

        return;
      }

      form.setValue(name, data);
    }
  }, [isMutate, name, form, parentControl]);

  const renderSelectedContent = useCallback((): React.JSX.Element => {
    switch (type) {
      case "time":
        return (
          <FormField
            control={form.control}
            name={name}
            render={({ field }) => (
              <TimePicker12
                date={new Date(field.value) ?? new Date()}
                setDate={(date) => {
                  field.onChange(date);
                }}
                showIcon
              />
            )}
          />
        );

      case "select":
        return (
          <FormField
            control={form.control}
            name={name}
            render={({ field }) => (
              <FormWrapper>
                <FormSelectSingle
                  placeholder='Please Select Employment Type'
                  field={field}
                  className='w-full'
                  options={list || []}
                />
              </FormWrapper>
            )}
          />
        );
      case "multiSelect":
        return (
          <SelectMulti
            name={name}
            control={form.control}
            placeholder={`Select ${name}`}
            defaultValue={form.getValues(name) || []}
            options={list || []}
            node={selectMultiNode}
            maxCount={2}
          />
        );
      default:
        return (
          <FormField
            control={form.control}
            name={name}
            render={({ field }) => (
              <InputField
                placeholder={`Enter ${name}`}
                type={type as "text" | "number" | "textarea"}
                rows={3}
                required
                {...field}
              />
            )}
          />
        );
    }
  }, [type, name, form, list]);

  if (parentControl ? parentControl?.isMutate : isMutate) {
    return (
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className='min-w-[200px]'>
          <div
            className='flex items-center'
            onClick={(e) => e.stopPropagation()}
          >
            <div className='flex-1'>
              {config?.label && (
                <FormLabel htmlFor={name} className='ml-1 font-normal w-fit'>
                  {config?.label}{" "}
                  {config?.required && (
                    <span className='text-red-500 font-thin text-xs leading-[8px]'>
                      {" "}
                      *
                    </span>
                  )}
                </FormLabel>
              )}
              {renderSelectedContent()}
            </div>
            <div className='flex flex-col gap-1 ml-2'>
              <Button
                type='submit'
                variant='ghost'
                size='icon'
                className='w-8 h-8'
              >
                <Check className='w-4 h-4' />
              </Button>
              <Button
                type='button'
                variant='ghost'
                size='icon'
                className='w-8 h-8'
                onClick={handleExit}
              >
                <X className='w-4 h-4' />
              </Button>
            </div>
          </div>
        </form>
      </Form>
    );
  }

  return (
    <div
      className={cn(
        "flex justify-between items-center",
        !config?.isTable && "group",
        classes?.containerClassName,
      )}
      onClick={(e) => e.stopPropagation()}
    >
      {config?.editPosition === "left" && config?.showEdit && (
        <EditButton
          config={config}
          handleEditClick={() => {
            if (parentControl) {
              parentControl.handleEdit();
            } else {
              setIsMutate(true);
            }
          }}
        />
      )}

      <div
        className={cn(
          "flex items-center justify-between",
          classes?.childClassName,
        )}
      >
        {children}
      </div>
      {config?.editPosition === "right" && config.showEdit && (
        <EditButton
          config={config}
          handleEditClick={() => {
            if (parentControl) {
              parentControl.handleEdit();
            } else {
              setIsMutate(true);
            }
          }}
        />
      )}
    </div>
  );
};
